// Spotify API mock data and client

// Spotify API configuration
const CLIENT_ID = '1ca727ebf3ab42e1977596bf81293fb5';
const CLIENT_SECRET = '4fdd4284c7004a45b5d1e9369dd4683e';

// Mock tracks för fallback
const MOCK_TRACKS = [
  {
    id: "mock001",
    name: "Digital Dreams",
    duration_ms: 240000,
    external_urls: {
      spotify: "https://open.spotify.com/track/mock001"
    },
    album: {
      name: "Virtual Visions",
      images: [
        {
          url: "https://via.placeholder.com/300",
          height: 300,
          width: 300
        }
      ],
      release_date: "2023-05-01"
    },
    artists: [
      { name: "AIMusicDreams" }
    ],
    preview_url: null
  },
  {
    id: "mock002",
    name: "Neural Harmony",
    duration_ms: 215000,
    external_urls: {
      spotify: "https://open.spotify.com/track/mock002"
    },
    album: {
      name: "Virtual Visions",
      images: [
        {
          url: "https://via.placeholder.com/300",
          height: 300,
          width: 300
        }
      ],
      release_date: "2023-05-01"
    },
    artists: [
      { name: "AIMusicDreams" }
    ],
    preview_url: null
  }
];

/**
 * Get an access token from Spotify API
 */
async function getSpotifyToken() {
  try {
    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Basic ' + btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)
      },
      body: 'grant_type=client_credentials'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get token: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Error getting Spotify token:', error);
    throw new Error('Failed to get Spotify access token');
  }
}

/**
 * Get artist's top tracks (includes preview_url)
 */
async function getTopTracks(token: string, artistId: string) {
  try {
    const response = await fetch(
      `https://api.spotify.com/v1/artists/${artistId}/top-tracks?market=SE`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get top tracks: ${response.statusText}`);
    }
    
    const data = await response.json();
    // Top tracks API returns tracks with preview_url included
    return data.tracks;
  } catch (error) {
    console.error('Error getting top tracks:', error);
    throw new Error('Failed to fetch top tracks');
  }
}

/**
 * Get artist's latest albums and then their tracks with preview URLs
 */
async function getLatestTracks(token: string, artistId: string) {
  try {
    // Get the artist's albums
    const albumsResponse = await fetch(
      `https://api.spotify.com/v1/artists/${artistId}/albums?include_groups=single,album&limit=50&market=SE`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!albumsResponse.ok) {
      throw new Error(`Failed to get albums: ${albumsResponse.statusText}`);
    }

    const albumsData = await albumsResponse.json();

    // Sort newest first, take top 10 releases
    const releases = albumsData.items.slice();
    releases.sort((a: any, b: any) =>
      new Date(b.release_date).getTime() - new Date(a.release_date).getTime()
    );

    const albumIds = releases.slice(0, 10).map((s: any) => s.id);
    
    if (albumIds.length === 0) {
      return [];
    }
    
    // Get tracks for these albums (this includes preview_url)
    const tracksPromises = albumIds.map((albumId: string) =>
      fetch(`https://api.spotify.com/v1/albums/${albumId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then(res => res.json())
    );
    
    const albumsWithTracks = await Promise.all(tracksPromises);
    
    // Extract and flatten all tracks from all albums
    const tracks = albumsWithTracks.flatMap((album: any) => {
      return album.tracks.items.map((track: any) => {
        // Get full track details to include preview_url
        return {
          ...track,
          preview_url: track.preview_url,
          album: {
            name: album.name,
            images: album.images,
            release_date: album.release_date
          }
        };
      });
    });
    
    return tracks;
  } catch (error) {
    console.error('Error getting latest tracks:', error);
    throw new Error('Failed to fetch latest tracks');
  }
}

export interface PlaylistData {
  id: string;
  name: string;
  image: string | null;
  trackCount: number;
  url: string;
}

async function getPlaylistData(token: string, playlistId: string): Promise<PlaylistData> {
  const response = await fetch(
    `https://api.spotify.com/v1/playlists/${playlistId}?fields=id,name,images,tracks.total,external_urls`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error(`Failed to get playlist ${playlistId}: ${response.statusText}`);
  const data = await response.json();
  return {
    id: data.id,
    name: data.name,
    image: data.images?.[0]?.url ?? null,
    trackCount: data.tracks?.total ?? 0,
    url: data.external_urls?.spotify ?? `https://open.spotify.com/playlist/${playlistId}`,
  };
}

export async function getPlaylistsData(playlistIds: string[]): Promise<PlaylistData[]> {
  try {
    const token = await getSpotifyToken();
    return await Promise.all(playlistIds.map(id => getPlaylistData(token, id)));
  } catch (error) {
    console.error('Error fetching playlists:', error);
    return playlistIds.map(id => ({
      id,
      name: '',
      image: null,
      trackCount: 0,
      url: `https://open.spotify.com/playlist/${id}`,
    }));
  }
}

/**
 * Main function to get all Spotify data for the artist
 * Now accepts artistId as parameter and includes preview_url in tracks
 */
export async function getSpotifyData(artistId: string = '4mzl9kB6QtB7F2UZktujSY') {
  try {
    // Försök att hämta data från Spotify API
    const token = await getSpotifyToken();
    
    const [topTracks, latestTracks] = await Promise.all([
      getTopTracks(token, artistId),
      getLatestTracks(token, artistId)
    ]);
    
    return {
      topTracks,
      latestTracks
    };
  } catch (error) {
    console.error('Error in getSpotifyData:', error);
    // Fallback till mock-data vid fel
    console.warn('Using mock data as fallback');
    return {
      topTracks: MOCK_TRACKS.slice(0, 10),
      latestTracks: MOCK_TRACKS.slice(0, 5)
    };
  }
}